import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { adminApi, type Invitation } from "../api/admin";
import { rolesApi, type RoleView } from "../api/access";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";

/**
 * Invite form: e-mail plus the roles the new account starts with. The roles
 * come from `GET /api/admin/roles` (sprint 7 §5) — nothing is hard-coded.
 * Choosing roles is access assignment (§1): without `users:access` the form
 * sends the invitation alone and says who assigns the roles afterwards.
 * Per-user exceptions are set afterwards on the user's "Pristup" screen.
 */
export function InviteUserForm({
  onCreated,
  onCancel,
}: {
  onCreated: (inv: Invitation) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canAssign = hasPermission("users:access");
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState<RoleView[] | null>(canAssign ? null : []);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canAssign) return;
    let cancelled = false;
    rolesApi.list()
      .then((r) => { if (!cancelled) setRoles(r); })
      .catch((err) => { if (!cancelled) { setRoles([]); setError((err as ApiError).message); } });
    return () => { cancelled = true; };
  }, [canAssign]);

  function toggleRole(id: string) {
    setRoleIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (canAssign && roleIds.length === 0) {
      setError(t("inviteUser.error.pickRole", "Izaberite bar jednu ulogu."));
      return;
    }
    setSubmitting(true);
    try {
      const inv = await adminApi.createInvitation({ email: email.trim(), roleIds: canAssign ? roleIds : [] });
      onCreated(inv);
      setEmail("");
      setRoleIds([]);
    } catch (err) {
      setError(translateError(err as ApiError, t));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <label className="field">
        <span>{t("inviteUser.field.email", "Email")} <span className="req-mark">*</span></span>
        <input
          type="email"
          className="input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder={t("inviteUser.field.emailPlaceholder", "korisnik@bankom.rs")}
        />
      </label>

      {canAssign ? (
        <div className="field">
          <span>{t("inviteUser.field.roles", "Uloge")} <span className="req-mark">*</span></span>
          <div className="role-list">
            {roles === null ? (
              <div className="empty" style={{ padding: 16 }}>{t("common.loading", "Učitavanje…")}</div>
            ) : roles.length === 0 ? (
              <div className="empty" style={{ padding: 16 }}>{t("inviteUser.noRoles", "Nema definisanih uloga.")}</div>
            ) : (
              roles.map((r) => (
                <label key={r.id} className="check-row">
                  <input type="checkbox" checked={roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} aria-label={r.name} />
                  <span className="role-list-text">
                    <b>{r.name}</b>
                    {r.description && <span className="sub">{r.description}</span>}
                  </span>
                  <span className="role-count">
                    {r.allFunctions
                      ? t("roles.allFunctionsShort", "Sve")
                      : t("userAccess.roleFunctions", "{{count}} f.", { count: r.functionKeys.length + r.impliedKeys.length })}
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 12.5 }}>
          {t("inviteUser.rolesNoAccess", "Uloge na pozivnici dodeljuje korisnik sa funkcijom „Dodela uloga i funkcija“ — ovaj nalog će ih dobiti naknadno na svom ekranu „Pristup“.")}
        </div>
      )}

      {error && <div style={errorBox} role="alert">{error}</div>}

      <div className="form-actions">
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>
            {t("inviteUser.cancel", "Otkaži")}
          </button>
        )}
        <button type="submit" className="btn primary" disabled={submitting || roles === null}>
          {submitting ? t("inviteUser.submitBusy", "Slanje…") : t("inviteUser.submit", "Pošalji pozivnicu")}
        </button>
      </div>
    </form>
  );
}

function translateError(err: ApiError, t: (key: string, fallback: string) => string): string {
  switch (err.code) {
    case "USER_EXISTS":
      return t("inviteUser.error.userExists", "Korisnik sa ovim imejlom već postoji.");
    case "INVITATION_PENDING":
      return t("inviteUser.error.invitationPending", "Već postoji pozivnica na čekanju za ovaj imejl.");
    case "FORBIDDEN":
      return t("inviteUser.error.forbidden", "Nemate pravo da pozivate korisnike.");
    default:
      return err.message || t("inviteUser.error.generic", "Kreiranje pozivnice nije uspelo.");
  }
}

const errorBox: React.CSSProperties = {
  padding: 10,
  background: "rgba(182,53,58,.12)",
  color: "var(--err)",
  borderRadius: 6,
  fontSize: 13,
};
